import React, { useState, useEffect } from 'react';
import documentApi, { Document } from '../../api/documentApi';
import documentValidationApi, { ValidationData } from '../../api/documentValidationApi';
import userApi from '../../api/userApi';
import DocumentValidationWorkflowHeader from '../../components/archive/validation/DocumentValidationWorkflowHeader';
import DocumentValidationWorkflowStats from '../../components/archive/validation/DocumentValidationWorkflowStats';
import DocumentValidationWorkflowTabs from '../../components/archive/validation/DocumentValidationWorkflowTabs';
import DocumentValidationWorkflowModal from '../../components/archive/validation/DocumentValidationWorkflowModal';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  not_required: 'bg-gray-100 text-gray-700',
};

export default function DocumentValidationWorkflow() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [validationModal, setValidationModal] = useState<{ open: boolean; doc: Document | null }>({
    open: false,
    doc: null,
  });
  const [formData, setFormData] = useState({ notes: '', reason: '' });

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const docs = await documentApi.getAll({ is_deleted: false });
      // Sort by created_date descending
      const sortedDocs = docs.sort((a, b) => {
        const dateA = new Date(a.created_date || 0).getTime();
        const dateB = new Date(b.created_date || 0).getTime();
        return dateB - dateA;
      });
      setDocuments(sortedDocs);
    } catch (error) {
      console.error('Error loading:', error);
    } finally {
      setLoading(false);
    }
  };

  const openValidationModal = (doc: Document) => {
    setValidationModal({ open: true, doc });
    setFormData({ notes: '', reason: '' });
  };

  const closeValidationModal = () => {
    setValidationModal({ open: false, doc: null });
  };

  const handleValidate = async (status: 'approve' | 'reject') => {
    if (!validationModal.doc) return;

    try {
      // Get current user
      const user = await userApi.getCurrentUser();

      const validationData: ValidationData = {
        status: status === 'approve' ? 'valid' : 'rejected',
        validation_status: status === 'approve' ? 'approved' : 'rejected',
        validated_by: user.id,
        validated_by_name: user.full_name,
        validation_date: new Date().toISOString().split('T')[0],
        validation_notes: formData.notes,
        rejection_reason: status === 'reject' ? formData.reason : undefined,
      };

      // Update document
      await documentValidationApi.validateDocument(validationModal.doc.id, validationData);

      // Create audit log
      await documentValidationApi.createAuditLog({
        document_id: validationModal.doc.id,
        action: status,
        actor_id: user.id,
        actor_name: user.full_name,
        actor_role: user.role,
        description:
          status === 'approve'
            ? `Approved document: ${validationModal.doc.document_name || validationModal.doc.file_name}`
            : `Rejected document: ${validationModal.doc.document_name || validationModal.doc.file_name}`,
      });

      closeValidationModal();
      await loadDocuments();
    } catch (error: any) {
      console.error('Error validating:', error);
      alert('Error: ' + (error.message || 'Gagal memvalidasi dokumen'));
    }
  };

  const pendingDocs = documents.filter(d => d.validation_status === 'pending');
  const approvedDocs = documents.filter(d => d.validation_status === 'approved');
  const rejectedDocs = documents.filter(d => d.validation_status === 'rejected');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DocumentValidationWorkflowHeader />

      <DocumentValidationWorkflowStats
        pendingCount={pendingDocs.length}
        approvedCount={approvedDocs.length}
        rejectedCount={rejectedDocs.length}
      />

      <DocumentValidationWorkflowTabs
        pendingDocs={pendingDocs}
        approvedDocs={approvedDocs}
        rejectedDocs={rejectedDocs}
        statusColors={statusColors}
        onReview={openValidationModal}
      />

      <DocumentValidationWorkflowModal
        open={validationModal.open}
        document={validationModal.doc}
        formData={formData}
        onClose={closeValidationModal}
        onApprove={() => handleValidate('approve')}
        onReject={() => handleValidate('reject')}
        onFormDataChange={setFormData}
      />
    </div>
  );
}
